package com.schoolconnect.app.feature.timetable

/** Compares labels naturally so numeric class and section values sort by magnitude. */
internal fun compareTimetableLabelsNaturally(left: String, right: String): Int {
    var leftIndex = 0
    var rightIndex = 0

    while (leftIndex < left.length && rightIndex < right.length) {
        val leftChar = left[leftIndex]
        val rightChar = right[rightIndex]

        if (leftChar.isDigit() && rightChar.isDigit()) {
            val leftEnd = digitRunEnd(left, leftIndex)
            val rightEnd = digitRunEnd(right, rightIndex)
            val leftSignificantStart = significantDigitStart(left, leftIndex, leftEnd)
            val rightSignificantStart = significantDigitStart(right, rightIndex, rightEnd)
            val leftSignificantLength = leftEnd - leftSignificantStart
            val rightSignificantLength = rightEnd - rightSignificantStart

            if (leftSignificantLength != rightSignificantLength) {
                return leftSignificantLength.compareTo(rightSignificantLength)
            }

            for (offset in 0 until leftSignificantLength) {
                val digitComparison = left[leftSignificantStart + offset].digitToInt()
                    .compareTo(right[rightSignificantStart + offset].digitToInt())
                if (digitComparison != 0) return digitComparison
            }

            // Keep labels with fewer leading zeroes first when their numeric value matches.
            val runLengthComparison = (leftEnd - leftIndex).compareTo(rightEnd - rightIndex)
            if (runLengthComparison != 0) return runLengthComparison
            leftIndex = leftEnd
            rightIndex = rightEnd
        } else {
            val charComparison = leftChar.lowercaseChar().compareTo(rightChar.lowercaseChar())
            if (charComparison != 0) return charComparison
            leftIndex++
            rightIndex++
        }
    }

    return (left.length - leftIndex).compareTo(right.length - rightIndex)
}

internal fun compareTimetableClassSections(
    leftClassName: String,
    leftSectionName: String,
    rightClassName: String,
    rightSectionName: String
): Int {
    val classComparison = compareTimetableLabelsNaturally(leftClassName.trim(), rightClassName.trim())
    if (classComparison != 0) return classComparison
    return compareTimetableLabelsNaturally(leftSectionName.trim(), rightSectionName.trim())
}

private fun digitRunEnd(value: String, start: Int): Int {
    var end = start
    while (end < value.length && value[end].isDigit()) end++
    return end
}

private fun significantDigitStart(value: String, start: Int, end: Int): Int {
    var first = start
    while (first < end - 1 && value[first] == '0') first++
    return first
}
